import { useState } from 'react'
import { DismissibleDialog } from '@scoir/modals'
import { Box, Button, CircularProgress, Typography } from '@material-ui/core'

const { Body, Footer } = DismissibleDialog

interface BatchFile {
  Id: string
  Filename: string
  SystemState: string
}

interface BatchFileSelectionProps {
  files: BatchFile[]
  onDownload: (fileId: string, fileName: string) => Promise<void>
}

const BatchFileSelectionModalContent = ({ files, onDownload }: BatchFileSelectionProps) => {
  const [loading, setLoading] = useState<Record<string, boolean>>({})

  const getBatchFileName = (batchFileName: string) => {
    const fileNameParts = batchFileName.split('/')
    if (fileNameParts.length > 1) {
      return `scoir-documents-${fileNameParts[1]}`
    } else {
      return batchFileName
    }
  }

  const renderBatchFileRow = (file: BatchFile) => {
    const fileName = getBatchFileName(file.Filename)
    return (
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        py={1}
      >
        <Typography>{fileName}</Typography>
        <Box>
          {loading[file.Id] && (
            <>
              <CircularProgress
                variant="indeterminate"
                size={16}
                aria-hidden
              />
              &nbsp;
            </>
          )}
          <Button
            variant={file.SystemState === 'delivered' ? 'outlined' : 'contained'}
            color="primary"
            disabled={loading[file.Id]}
            onClick={() => {
              setLoading((original) => ({ ...original, [file.Id]: true }))
              onDownload(file.Id, fileName).finally(() => setLoading((original) => ({ ...original, [file.Id]: false })))
            }}
          >
            Download
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <>
      <Body>
        <Box mb={2}>
          <Typography>
            Due to its size, this batch was split into multiple files for delivery. Please select which file(s) you
            would like to download.
          </Typography>
        </Box>
        {files.map((file: BatchFile) => renderBatchFileRow(file))}
      </Body>
      <Footer>
        <Footer.CloseButton>Close</Footer.CloseButton>
      </Footer>
    </>
  )
}

interface BatchFileSelectionModalProps extends BatchFileSelectionProps {
  title: string
  open: boolean
  onCancel: () => void
  showScrim?: boolean
}

const BatchFileSelectionModal = ({ open, onCancel, title, showScrim, ...rest }: BatchFileSelectionModalProps) => (
  <DismissibleDialog
    open={open}
    onClose={onCancel}
    scroll="paper"
    headerText={title}
    maxWidth="md"
  >
    <BatchFileSelectionModalContent {...rest} />
  </DismissibleDialog>
)
export default BatchFileSelectionModal
